import CAIcon from '@ds/components/shared/CAIcon'
import DataTableActions from '@vuejs/components/shared/DataTableActions'
import type { PackageType } from '@vuejs/services/packages/interfaces'
import {
	defineComponent,
	type PropType,
	ref,
	watch,
} from 'vue'
import Vue3TreeVue from 'vue3-tree-vue'
import type { TreeViewItem } from 'vue3-tree-vue/dist/types/src/types'

import type { ActionElements } from '@/javascript/interfaces'

import NoResultsFoundPlaceholder from '../common/NoResultsFoundPlaceholder'
import { stringToNumber } from '../packages/utils'

export default defineComponent({
	name: 'TreeView',

	props: {
		additionalClasses: { default: '', type: String },
		autoCheckChildren: { default: true, type: Boolean },
		checkOnSelect: { default: false, type: Boolean },
		checkbox: { default: false, type: Boolean },
		data: { required: true, type: Array as PropType<Folder[]> },
		dataTest: { default: 'tree-view', type: String },
		message: { default: '', type: String },
		multiple: { default: true, type: Boolean },
		parentSelect: { default: false, type: Boolean },
	},

	emits: {
		action: (_action: string, _type: PackageType | string, _id?: number | string) => true,
		bulk: (_checkedNodes: CheckedNodes[]) => true,
	},

	setup(props, { emit }) {
		const checkedTreeNodes = ref<CheckedNodes[]>([])

		const transformData = (data: Folder[]): TreeViewItem[] => {
			return data.map(item => ({
				checked: item.state?.checked || false,
				children: item.children ? transformData(item.children) : undefined,
				disabled: false,
				expanded: false,
				id: item.id as number | string,
				meta: {
					actions: item.data?.actions,
					originalData: item.data,
					state: item.state,
					type: item.type,
				},
				name: item.text,
			}))
		}

		const treeData = ref<TreeViewItem[]>(transformData(props.data))

		watch(() => props.data, newData => {
			treeData.value = transformData(newData)
		}, { deep: true })

		const handleOnActionClick = (
			event: Event,
			action: string,
			type: PackageType | string,
			id?: number | string
		): void => {
			event.stopPropagation()

			emit('action', action, type, !!id ? stringToNumber(id) : undefined)
		}

		const onItemChecked = (checkedItems: TreeViewItem[]): void => {
			checkedTreeNodes.value = checkedItems.map(item => ({
				id: item.id,
				type: item.meta?.type || '',
			}))

			emit('bulk', checkedTreeNodes.value)
		}

		const onItemSelected = (item: TreeViewItem): void => {
			if (props.checkOnSelect) {
				item.checked = !item.checked

				onItemChecked(treeData.value.filter(node => isNodeChecked(node)))
			}
		}

		const isNodeChecked = (node: TreeViewItem): boolean => {
			if (node.checked) {
				return true
			}

			if (node.children) {
				return node.children.some(child => isNodeChecked(child))
			}

			return false
		}

		const onItemExpanded = (expandedItem: TreeViewItem): void => {
			// Handle lazy loading here if needed
			console.info('Item expanded:', expandedItem)
		}

		return {
			treeData,
			checkedTreeNodes,
			handleOnActionClick,
			onItemChecked,
			onItemSelected,
			onItemExpanded,
		}
	},

	render() {
		return (
			<div class={this.additionalClasses} data-test={this.dataTest}>
				<Vue3TreeVue
					ref="tree"
					disableDragAndDrop={true}
					hideGuideLines={false}
					isCheckable={this.checkbox}
					items={this.treeData}
					v-slots={{
						'item-prepend-icon': (treeViewItem: TreeViewItem) => {
							if (treeViewItem.meta?.type === 'content_package_folder') {
								return <CAIcon icon="SVGFolderFilled" />
							}

							return null
						},
						'item-append': (treeViewItem: TreeViewItem) => {
							if (treeViewItem.meta?.actions) {
								return (
									<span class="node-actions">
										{treeViewItem.meta.actions.length > 1 ?
											<DataTableActions
												actions={treeViewItem.meta.actions}
												id={`${treeViewItem.meta.originalData.id}`}
												onAction={(
													action: string,
													type: PackageType | string,
													id?: number | string
												): void =>
													this.$emit('action', action, type, id)}
											/>

											:

											<a
												href={treeViewItem.meta.actions[0].link ?? undefined}
												class="custom-menu__control-button"
												data-package-folder-id={treeViewItem.id}
												data-test={treeViewItem.meta.actions[0]?.dataTest || 'tree-view-action'}
												v-tooltip={treeViewItem.meta.actions[0].tooltip}
												onClick={event => {
													if (!treeViewItem.meta.actions[0].link) {
														event.preventDefault()
													}
													this.handleOnActionClick(
														event,
														treeViewItem.meta.actions[0].text ?? '',
														treeViewItem.meta.type,
														treeViewItem.id
													)
												}}
											>
												<CAIcon icon={treeViewItem.meta.actions[0].icon} />
											</a>
										}
									</span>
								)
							}

							return null
						},
					}}
					onOnCheck={this.onItemChecked}
					onOnExpand={this.onItemExpanded}
					onOnSelect={this.onItemSelected}
				/>

				{!this.data.length && <NoResultsFoundPlaceholder text={this.message} />}
			</div>
		)
	},
})

export interface CheckedNodes {
	id: number | string | undefined
	name?: string
	type: PackageType | string
}

export interface Folder {
	children?: {
		data: DataOptions
		id: number | string | undefined
		state: NodeStates
		text: string
		type: PackageType
	}[]
	data: DataOptions
	id: number | string | undefined
	state: NodeStates
	text: string
	type: PackageType
}

export interface NodeStates {
	checked: boolean
	visible: boolean
}

interface DataOptions {
	actions: ActionElements[]
	id: number | string
	type: PackageType
}
